import PropTypes from 'prop-types';
// @material-ui/core
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';

import {
  Analytics,
  ChevronLeft,
  ChevronRight,
  NotificationsActive,
  Search,
  Task,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

const drawerWidth = 225;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(8)} + 1px)` },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [{
      props: (
        { open },
      ) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    }, {
      props: (
        { open },
      ) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    }],
  }),
);

const pages = [
  {
    title: 'Search Tasks',
    path: '/tasks',
    staff: true,
    admin: true,
    owner: true,
    icon: <Search />,
  },
  {
    title: 'Reminders',
    path: '/tasks/reminders',
    staff: true,
    admin: true,
    owner: true,
    icon: <NotificationsActive />,
  },
  {
    title: 'Analytics',
    path: '/tasks/analytics',
    staff: true,
    admin: true,
    owner: true,
    icon: <Analytics />,
  },
];

const NavigationBarnComponent = ({
  navigate,
  path,
  open,
  setOpen,
}) => {
  const permissions = useSelector((state) => state.user.permissions)

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader sx={{ pt: '3rem' }}>
        {open && (
          <Box
            sx={{
              textAlign: 'center',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              my: 1,
            }}
          >
            <Task />
            <Typography
              variant="h5"
              sx={{
                ml: 1,
                fontWeight: 600,
                my: 1,
              }}
            >Tasks
            </Typography>
          </Box>
        )}
        <Tooltip
          title={open
            ? 'Hide Drawer'
            : 'Show Drawer'}
        >
          <IconButton
            sx={{
              my: 1,
              mr: !open
                ? '6px !important'
                : 0,
            }}
            size="small"
            onClick={() => { if (open) { handleDrawerClose(); } else { handleDrawerOpen(); } }}
          >
            {!open
              ? <ChevronRight />
              : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </DrawerHeader>
      <Divider />
      <List sx={{ p: 0 }}>
        {
          pages.filter((p) => (p.owner && permissions.owner)
            || (p.admin && permissions.admin)
            || (p.staff && permissions.staff))
            .map((page) => {
              let tooltipTitle = page.title;
              if (open) {
                tooltipTitle = '';
              }

              return (
                <ListItem key={page.title} disablePadding sx={{ display: 'block' }} divider>
                  <Tooltip title={tooltipTitle}>
                    <ListItemButton
                      onClick={() => { navigate(page.path); }}
                      selected={page.path === path && !page.children}
                      sx={[{
                        minHeight: 48,
                        px: 2.5,
                      }, open
                        ? { justifyContent: 'initial' }
                        : { justifyContent: 'center' }]}
                    >
                      <ListItemIcon
                        sx={[{
                          minWidth: 0,
                          justifyContent: 'center',
                          pl: '12px',
                          '& .MuiSvgIcon-root': {
                            width: '22px !important',
                            height: '22px !important',
                          },
                        }, open
                          ? { mr: 3 }
                          : { mr: 'auto' }]}
                      >
                        {page.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={page.title}
                        primaryTypographyProps={{
                          fontWeight: page.path === path
                            ? 600
                            : 400,
                        }}
                        sx={[open
                          ? { opacity: 1 }
                          : {
                            opacity: 0,
                            display: 'none',
                          }]}
                      />
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })
        }
      </List>
    </Drawer>
  );
}

NavigationBarnComponent.propTypes = {
  navigate: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default NavigationBarnComponent;
